import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as RevokeIcon,
  Refresh as RefreshIcon,
  VpnKey as KeyIcon,
  PowerSettingsNew as DisconnectIcon,
  SmartToy as ConnectorIcon,
} from '@mui/icons-material';
import {
  adminApi,
  ApiKeySummary,
  ApiKeyCreationResponse,
  McpConnectorSummary,
} from '../services/api';
import {
  connectorLastUsedLabel,
  connectorLastUsedSortValue,
  connectorStatusLabel,
} from './apiKeysConnectorCells';
import { useAuth } from '../contexts/AuthContext';
import CreateApiKeyDialog from '../components/CreateApiKeyDialog';
import RevealApiKeyDialog from '../components/RevealApiKeyDialog';
import ConfirmRevokeDialog from '../components/ConfirmRevokeDialog';
import ConfirmDisconnectDialog from '../components/ConfirmDisconnectDialog';

const ApiKeysPage: React.FC = () => {
  const { user } = useAuth();
  const orgId = user?.organizationId;

  const [keys, setKeys] = useState<ApiKeySummary[]>([]);
  const [connectors, setConnectors] = useState<McpConnectorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<ApiKeyCreationResponse | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<McpConnectorSummary | null>(
    null,
  );

  const fetchKeys = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.listApiKeys(orgId);
      setKeys(response.keys || []);
      setConnectors(response.connectors || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreated = (created: ApiKeyCreationResponse) => {
    setCreateOpen(false);
    setRevealedKey(created);
    fetchKeys();
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget || !orgId) return;
    try {
      await adminApi.revokeApiKey(orgId, revokeTarget.keyId);
      setRevokeTarget(null);
      fetchKeys();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to revoke API key');
      setRevokeTarget(null);
    }
  };

  // The SAME endpoint as a key revoke — one credential store, one revoke. Only
  // the confirmation wording differs, because the consequence does: this cuts a
  // colleague's AI client off rather than rotating an org secret.
  const handleDisconnectConfirm = async () => {
    if (!disconnectTarget || !orgId) return;
    try {
      await adminApi.revokeApiKey(orgId, disconnectTarget.keyId);
      setDisconnectTarget(null);
      fetchKeys();
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || 'Failed to disconnect the AI client',
      );
      setDisconnectTarget(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'keyName',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'keyPrefix',
      headerName: 'Prefix',
      width: 160,
      renderCell: (params: GridRenderCellParams<ApiKeySummary>) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {params.value ? `${params.value}…` : '—'}
        </Typography>
      ),
    },
    {
      field: 'scopes',
      headerName: 'Scopes',
      flex: 1.4,
      minWidth: 240,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ApiKeySummary>) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ py: 0.5 }}>
          {(params.value as string[] || []).map((scope) => {
            const isPrivileged =
              scope === 'users:create' || scope === 'admin:read' || scope === 'admin:write';
            return (
              <Chip
                key={scope}
                label={scope}
                size="small"
                color={isPrivileged ? 'warning' : 'default'}
                variant={isPrivileged ? 'filled' : 'outlined'}
              />
            );
          })}
        </Stack>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams<ApiKeySummary>) => {
        const status = params.value as string;
        const color =
          status === 'active' ? 'success' :
          status === 'revoked' ? 'default' :
          'warning';
        return <Chip label={status} size="small" color={color as any} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 130,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString() : '',
    },
    {
      field: 'rateLimit',
      headerName: 'Rate Limit',
      width: 120,
      renderCell: (params: GridRenderCellParams<ApiKeySummary>) => (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {params.row.rateLimit}/{params.row.rateLimitWindow}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<ApiKeySummary>) => {
        const disabled = params.row.status !== 'active';
        return (
          <Tooltip title={disabled ? 'Already revoked' : 'Revoke this key'}>
            <span>
              <IconButton
                color="error"
                size="small"
                disabled={disabled}
                onClick={() => setRevokeTarget(params.row)}
                aria-label={`Revoke ${params.row.keyName}`}
              >
                <RevokeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const connectorColumns: GridColDef[] = [
    {
      field: 'keyName',
      headerName: 'Connector',
      flex: 1.4,
      minWidth: 260,
    },
    {
      // `field` is the raw status; `valueGetter` is what the chip says. Without
      // the getter, filtering this column to 'Disconnected by an admin' would
      // match nothing — the grid would be comparing against 'revoked'.
      field: 'status',
      headerName: 'Status',
      width: 240,
      valueGetter: (_value, row) => connectorStatusLabel(row as McpConnectorSummary),
      renderCell: (params: GridRenderCellParams<McpConnectorSummary>) => (
        <Chip
          label={connectorStatusLabel(params.row)}
          size="small"
          color={params.row.status === 'active' ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Connected',
      width: 130,
      valueFormatter: (value: string) => (value ? new Date(value).toLocaleDateString() : ''),
    },
    {
      field: 'lastUsedAt',
      headerName: 'Last used',
      width: 170,
      type: 'dateTime',
      // The getter feeds SORT and FILTER, so it returns the moment; the cell
      // renders the label, which additionally says whether that moment is a
      // last use or a cut-off. A formatted string in the getter sorted this
      // column alphabetically.
      valueGetter: (_value, row) =>
        connectorLastUsedSortValue(row as McpConnectorSummary),
      renderCell: (params: GridRenderCellParams<McpConnectorSummary>) => (
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {connectorLastUsedLabel(params.row)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<McpConnectorSummary>) => {
        const disabled = params.row.status !== 'active';
        return (
          <Tooltip title={disabled ? 'Already disconnected' : 'Disconnect this AI client'}>
            <span>
              <IconButton
                color="error"
                size="small"
                disabled={disabled}
                onClick={() => setDisconnectTarget(params.row)}
                aria-label={`Disconnect ${params.row.keyName}`}
              >
                <DisconnectIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  if (!orgId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Your account is not linked to an organisation. API keys cannot be loaded.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            API Keys
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchKeys}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Create Key
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Keys for organisation: <strong>{orgId}</strong>. Privileged scopes
        (highlighted in orange) require Admin group membership.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ p: 0 }}>
        {loading && keys.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={keys}
            columns={columns}
            getRowId={(row) => row.keyId}
            disableRowSelectionOnClick
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
            }}
            sx={{
              border: 'none',
              // Vertically centre the content of every cell. MUI X DataGrid v7
              // renders custom `renderCell` content as a block by default, so
              // a tall cell (e.g. the wrapping Scopes column) leaves siblings
              // top-aligned without these rules.
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              // The default value-formatted cells (Name, Created) wrap their
              // text in a separate inner div — centre that too.
              '& .MuiDataGrid-cellContent': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        )}
      </Card>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 4, mb: 1 }}>
        <ConnectorIcon sx={{ color: 'text.secondary' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Connected AI clients
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Credentials members authorised by signing in from Claude, ChatGPT or
        another AI client. Each one can read that person’s jobs and download
        their documents. Disconnecting takes effect immediately; one switched off
        by disabling the account comes back if the account is re-enabled.
      </Typography>

      <Card sx={{ p: 0 }} data-testid="connected-ai-clients">
        {connectors.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Nobody in this organisation has connected an AI client.
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={connectors}
            columns={connectorColumns}
            getRowId={(row) => row.keyId}
            disableRowSelectionOnClick
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
              '& .MuiDataGrid-cellContent': { display: 'flex', alignItems: 'center' },
            }}
          />
        )}
      </Card>

      <CreateApiKeyDialog
        open={createOpen}
        orgId={orgId}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      <RevealApiKeyDialog
        keyData={revealedKey}
        onClose={() => setRevealedKey(null)}
      />

      <ConfirmRevokeDialog
        target={revokeTarget}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
      />

      <ConfirmDisconnectDialog
        target={disconnectTarget}
        onCancel={() => setDisconnectTarget(null)}
        onConfirm={handleDisconnectConfirm}
      />
    </Box>
  );
};

export default ApiKeysPage;
